import { FormControl, FormHelperText, InputLabel, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { type ReactNode, useId } from 'react';
import { type SelectOption } from './ProjectFieldsModal.types';

type SelectFieldProps<T> = {
    label: string;
    options: SelectOption<T>[];
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    helperText?: ReactNode;
    fullWidth?: boolean;
    // 'outlined' matches the ticket mockups and the Grading Portal filter bar.
    variant?: 'filled' | 'outlined';
};

// A single-select built on MUI, to match the Grading Portal's ProgramTypeFilterSelect. An option
// value can be a number, a string, a boolean, or null, but an MUI Select value must be a string. So
// the option index is the select value, and onChange decodes it back to the typed value. MUI puts its
// menu in a portal, so this works inside a Dialog.
export function SelectField<T>({
    label,
    options,
    value,
    onChange,
    disabled,
    helperText,
    fullWidth = true,
    variant = 'outlined',
}: SelectFieldProps<T>) {
    const labelId = useId();
    const selectedIndex = options.findIndex(option => option.value === value);
    const selectedKey = selectedIndex >= 0 ? String(selectedIndex) : '';

    const handleChange = (event: SelectChangeEvent) => {
        const key = event.target.value;
        if (key !== '') onChange(options[Number(key)].value);
    };

    return (
        <FormControl fullWidth={fullWidth} size="small" variant={variant} disabled={disabled}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select labelId={labelId} label={label} value={selectedKey} onChange={handleChange}>
                {options.map((option, index) => (
                    <MenuItem key={index} value={String(index)}>
                        {option.label || '—'}
                    </MenuItem>
                ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
}
